import React, { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { getLiveStreams } from "../../lib/api";
import { createLiveStream, deleteLiveStream, updateLiveStream } from "../../lib/adminApi";
import { getAdminErrorMessage } from "../../lib/adminForm";
import { AdminField, AdminModal, AdminTable } from "../../components/admin/AdminUi";
import { MediaInput } from "../../components/admin/MediaInput";

const EMPTY = {
  title: "",
  description: "",
  coverImage: "",
  embedUrl: "",
  isActive: false,
  isMain: false,
  viewers: "",
  order: 0,
};

export default function AdminLive() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => getLiveStreams().then(setItems).catch(() => setItems([]));

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setError("");
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...item });
    setError("");
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 0,
        isActive: Boolean(form.isActive),
        isMain: Boolean(form.isMain),
      };
      if (editing) await updateLiveStream(editing.id, payload);
      else await createLiveStream(payload);
      setOpen(false);
      try {
        await load();
      } catch {
        // Kayıt başarılı; liste yenileme hatası kayıt hatası gibi gösterilmez.
      }
    } catch (err) {
      setError(getAdminErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu canlı yayın silinecek.")) return;
    await deleteLiveStream(id);
    await load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-5">
        <h2 className="font-display font-bold text-2xl text-white">Canlı Yayınlar</h2>
        <button type="button" onClick={openCreate} className="tv-btn-primary !py-2.5 text-sm inline-flex items-center gap-2">
          <Plus size={16} /> Yeni Canlı Yayın
        </button>
      </div>

      <AdminTable columns={["Yayın", "Durum", "İzleyici", "Sıra", ""]}>
        {items.map((item) => (
          <tr key={item.id} className="border-b border-[var(--tv-border)] last:border-0">
            <td className="px-4 py-3">
              <p className="font-semibold text-white">{item.title}</p>
              <p className="text-xs text-[var(--tv-muted)] mt-0.5 line-clamp-1">{item.description}</p>
            </td>
            <td className="px-4 py-3">
              <span className={item.isActive ? "tv-badge-live text-xs" : "tv-badge text-xs"}>
                {item.isActive ? "CANLI" : "YAKINDA"}
              </span>
              {item.isMain && <span className="ml-2 text-xs text-[var(--tv-purple-bright)]">Ana yayın</span>}
            </td>
            <td className="px-4 py-3 text-[var(--tv-muted)]">{item.viewers}</td>
            <td className="px-4 py-3 text-[var(--tv-muted)]">{item.order}</td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => openEdit(item)} className="tv-btn-ghost !py-1.5 !px-2.5 text-xs">
                  <Pencil size={14} />
                </button>
                <button type="button" onClick={() => remove(item.id)} className="tv-btn-ghost !py-1.5 !px-2.5 text-xs text-red-300">
                  <Trash2 size={14} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </AdminTable>

      <AdminModal open={open} title={editing ? "Canlı Yayın Düzenle" : "Yeni Canlı Yayın"} onClose={() => setOpen(false)}>
        <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
          <AdminField label="Başlık" className="md:col-span-2">
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="tv-input" />
          </AdminField>
          <AdminField label="Açıklama" className="md:col-span-2">
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="tv-input resize-none" />
          </AdminField>
          <AdminField label="YouTube / Embed URL" className="md:col-span-2">
            <input value={form.embedUrl} onChange={(e) => setForm({ ...form, embedUrl: e.target.value })} className="tv-input" placeholder="https://youtube.com/watch?v=..." />
          </AdminField>
          <MediaInput
            label="Kapak görseli"
            className="md:col-span-2"
            value={form.coverImage}
            onChange={(coverImage) => setForm({ ...form, coverImage })}
          />
          <AdminField label="İzleyici sayısı">
            <input value={form.viewers} onChange={(e) => setForm({ ...form, viewers: e.target.value })} className="tv-input" placeholder="2.180" />
          </AdminField>
          <AdminField label="Sıra">
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="tv-input" />
          </AdminField>
          <div className="md:col-span-2 flex flex-wrap gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm text-white">
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Canlı (aktif)
            </label>
            <label className="flex items-center gap-2 text-sm text-white">
              <input type="checkbox" checked={form.isMain} onChange={(e) => setForm({ ...form, isMain: e.target.checked })} />
              Ana yayın
            </label>
          </div>
          {error && <p className="md:col-span-2 text-sm text-red-400">{error}</p>}
          <div className="md:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setOpen(false)} className="tv-btn-ghost">İptal</button>
            <button type="submit" disabled={saving} className="tv-btn-primary">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </form>
      </AdminModal>
    </div>
  );
}
